/**
 * Builds parent and linked-sibling J-code document update payloads.
 * @module buildAllJCodeDocumentUpdatePayloads
 */

import { JCodeDocumentUpdatePayload } from "../../../components/modals/narLetterModal/utils/generateDocumentUpdatePayloads";
import {
  AuthOnFile,
  JCodeDetails,
} from "../../../data-model/medicalPaOrdersModel";
import {
  SiblingOrder,
  SiblingOrdersResponse,
} from "../../../data-model/siblingOrdersModel";
import { getAllJCodesFromAuthOnFile } from "../../../pages/singleOrderLayout/utils/existingDocsToLs";

/**
 * Finds a parent-order J-code whose `id` or `j_code` matches a payload id.
 *
 * @param jCodeId - `j_code_id` from a document update payload
 * @param parentOrderAuthOnFile - Parent order auth-on-file snapshot
 * @returns Matching parent J-code, or `undefined` when no row matches
 */
const findParentJCodeByPayloadId = (
  jCodeId: string,
  parentOrderAuthOnFile: AuthOnFile,
): JCodeDetails | undefined => {
  const parentJCodes = getAllJCodesFromAuthOnFile(parentOrderAuthOnFile) ?? [];

  return parentJCodes.find(
    (jCode) => jCode.id === jCodeId || jCode.j_code === jCodeId,
  );
};

/**
 * Returns parent-order J-codes aligned to each document update payload.
 *
 * @param payloads - Per-J-code document update payloads for the parent order
 * @param parentOrderAuthOnFile - Parent order auth-on-file snapshot
 * @returns Parent J-codes matched in payload order
 */
export const getParentJCodesMatchingPayloadIds = (
  payloads: JCodeDocumentUpdatePayload[],
  parentOrderAuthOnFile: AuthOnFile,
): JCodeDetails[] => {
  return payloads.flatMap((payload) => {
    const parentJCode = findParentJCodeByPayloadId(
      payload.j_code_id,
      parentOrderAuthOnFile,
    );

    return parentJCode ? [parentJCode] : [];
  });
};

/** Maps each sibling order id to matched J-code row ids on that order. */
export type SiblingJCodeIdsByOrderId = Record<string, string[]>;

/**
 * Resolves sibling-order J-code row ids for a shared set of parent J-codes.
 *
 * @param parentJCodeValues - Parent J-code strings used to match sibling rows
 * @param siblingOrders - Filtered sibling orders from the data accumulator
 * @returns Map of sibling order id → matched J-code row ids on that order
 */
export const mapSiblingJCodeIdsByOrderId = (
  parentJCodeValues: string[],
  siblingOrders: SiblingOrder[],
): SiblingJCodeIdsByOrderId => {
  return siblingOrders.reduce<SiblingJCodeIdsByOrderId>((acc, sibling) => {
    const siblingJCodes =
      getAllJCodesFromAuthOnFile(sibling.auth_on_file) ?? [];

    acc[sibling.order_id] = parentJCodeValues.map((parentJCodeValue) => {
      if (!parentJCodeValue) {
        return "";
      }

      const matchingSiblingJCode = siblingJCodes.find(
        (siblingJCode) => siblingJCode.j_code === parentJCodeValue,
      );

      return matchingSiblingJCode?.id ?? "";
    });

    return acc;
  }, {});
};

/**
 * Builds sibling-order document update payloads from parent payloads and a
 * pre-resolved sibling J-code id map.
 *
 * @param parentPayloads - Document update payloads for the parent order
 * @param siblingJCodeIdsByOrderId - Sibling order id → J-code row ids aligned
 *   to {@link parentPayloads}
 * @returns One payload per matched sibling J-code row
 */
export const buildSiblingDocumentUpdatePayloads = (
  parentPayloads: JCodeDocumentUpdatePayload[],
  siblingJCodeIdsByOrderId: SiblingJCodeIdsByOrderId,
): JCodeDocumentUpdatePayload[] => {
  const siblingPayloads: JCodeDocumentUpdatePayload[] = [];

  for (const [siblingOrderId, siblingJCodeIds] of Object.entries(
    siblingJCodeIdsByOrderId,
  )) {
    siblingJCodeIds.forEach((siblingJCodeId, index) => {
      const parentPayload = parentPayloads[index];
      if (!siblingJCodeId || !parentPayload) {
        return;
      }

      siblingPayloads.push({
        order_id: siblingOrderId,
        org_id: parentPayload.org_id,
        j_code_id: siblingJCodeId,
        documents: parentPayload.documents,
      });
    });
  }

  return siblingPayloads;
};

/**
 * Combines parent payloads with linked-sibling payloads for document updates.
 *
 * @param parentPayloads - Document update payloads for the parent order
 * @param parentOrderAuthOnFile - Parent order auth-on-file snapshot
 * @param filteredSiblingOrdersDetails - Full filtered sibling orders response
 * @returns Parent payloads followed by matched sibling payloads
 */
export const buildAllJCodeDocumentUpdatePayloads = (
  parentPayloads: JCodeDocumentUpdatePayload[],
  parentOrderAuthOnFile: AuthOnFile,
  filteredSiblingOrdersDetails: SiblingOrdersResponse,
): JCodeDocumentUpdatePayload[] => {
  const parentJCodeValues = parentPayloads.map(
    (payload) =>
      findParentJCodeByPayloadId(payload.j_code_id, parentOrderAuthOnFile)
        ?.j_code ?? "",
  );

  const siblingJCodeIdsByOrderId = mapSiblingJCodeIdsByOrderId(
    parentJCodeValues,
    filteredSiblingOrdersDetails.siblings ?? [],
  );

  const siblingPayloads = buildSiblingDocumentUpdatePayloads(
    parentPayloads,
    siblingJCodeIdsByOrderId,
  );

  return [...parentPayloads, ...siblingPayloads];
};
